import { Dispatch, SetStateAction } from "react";
import { readString } from "react-papaparse";
import readXlsxFile from "read-excel-file";
import { UploadResult } from "@uppy/core";

import { callFunctionIfExists } from "../../utils/GeneralUtils";
import UppyFile from "../../components/Inputs/UppyFile";
import TenantAPI from "../../api/api/TenantAPI";

export const uppyPlugins = ["OneDrive", "GoogleDrive"];

interface Props {
  autoDelete?: boolean;
  onFileChange: Dispatch<SetStateAction<File | null>>;
  onDataChange: (data: any) => void;
  onHeadersChange: (headers: string[]) => void;
  onError: (error: string) => void;
  onClear: () => void;
  onDone: () => void;
  afterUpload: () => void;
}

const ExcelCSVInput = ({
  autoDelete = true,
  onFileChange,
  onDataChange,
  onHeadersChange,
  onError,
  onClear,
  onDone,
  afterUpload,
}: Props) => {
  return (
    <>
      <div className="d-flex justify-content-center">
        <UppyFile
          fileType="Data"
          plugins={uppyPlugins}
          width={300}
          multiFiles={false}
          afterSubmit={async (_, raw) => {
            await handleFileChange(
              raw as UploadResult<any, Record<string, string>>
            );
            afterUpload();
          }}
          includeRaw
          doneButtonHandler={onDone}
        />
      </div>
    </>
  );

  async function handleFileChange(
    uploadedRaw: UploadResult<any, Record<string, string>>
  ) {
    try {
      if (!uploadedRaw?.successful?.length) throw new Error("No file uploaded");
      const rawFile = uploadedRaw.successful[0];
      const fileRecord =
        // @ts-ignore
        uploadedRaw.successful[0].response?.body?.data?.recordID ?? null;
      const { result, newHeaders } = await getCSVHeaders(rawFile.data as File);
      callFunctionIfExists(onFileChange, (rawFile.data as File) ?? null);
      callFunctionIfExists(onDataChange, result);
      callFunctionIfExists(onHeadersChange, newHeaders ?? []);

      if (autoDelete) {
        TenantAPI.deleteFiles({
          type: "fileID",
          fileID: fileRecord ?? "",
          moduleAPIName: "",
          recordID: "",
          fieldAPIName: "",
        });
      }
    } catch (error) {
      console.error(error, "error");

      clearFile();
      if (error === "Invalid File Type") {
        callFunctionIfExists(
          onError,
          "Invalid file type. We currently support .csv and .xlsx files."
        );
      } else {
        callFunctionIfExists(
          onError,
          // @ts-ignore
          error?.message ??
            "An unexpected error occurred. Please try again, or try with a different file."
        );
      }
    }
  }

  function clearFile() {
    callFunctionIfExists(onClear);

    // TODO: Delete the file
  }
};

export async function getCSVHeaders(file: File) {
  if (file.type.includes("csv") || file.name.endsWith(".csv")) {
    const text = await file.text();

    // @ts-ignore
    const csvResult = readString(text);
    // @ts-ignore
    const result = csvResult.data;

    // @ts-ignore
    let newHeaders = csvResult.data[0];

    if (newHeaders.includes(null)) {
      // @ts-ignore
      newHeaders = csvResult.data[1];
    }

    if (newHeaders.includes(null)) {
      throw new Error("Headers row has empty headers!");
    }

    return { result, newHeaders };
  } else if (file?.name?.endsWith(".xlsx")) {
    const result = await readXlsxFile(file);

    let newHeaders = result[0];

    // @ts-ignore
    if (newHeaders.includes(null)) {
      newHeaders = result[1];
    }

    // @ts-ignore
    if (newHeaders.includes(null)) {
      throw new Error("Headers row has empty headers!");
    }

    return { result, newHeaders };
  } else {
    throw new Error("Invalid file provided.");
  }
}

export default ExcelCSVInput;
